import React from 'react';
import { Checkmark, ErrorOutline, Information } from '@carbon/icons-react';
import { showToast } from '@ditioas/web-ui';
import { useTranslation } from 'react-i18next';
import type { ExternalToast } from 'sonner';

import cn from 'libs/cn';

type AlertType = 'error' | 'warning' | 'success' | 'notification';
export type StatusMessage = ({
  heading,
  message,
  data,
}: {
  heading?: string;
  message: string;
  data?: ExternalToast;
}) => void;

export const showAlert = ({
  type,
  heading,
  message,
  content,
  data,
}: {
  type: AlertType;
  heading?: string;
  message?: string;
  content?: React.ReactNode;
  data?: ExternalToast;
}) =>
  showToast(
    <>
      <p className="w-full select-none">
        {type === 'success' && (
          <Checkmark
            className={cn(
              heading ? 'absolute top-3.5 right-4' : 'float-end',
              'ml-2 fill-green-600'
            )}
          />
        )}
        {type === 'error' && (
          <ErrorOutline
            className={cn(
              heading ? 'absolute top-3.5 right-4' : 'float-end',
              'fill-red-1000 ml-2'
            )}
          />
        )}
        {type === 'notification' && (
          <Information
            className={cn(
              heading ? 'absolute top-3.5 right-4' : 'float-end',
              'fill-azure-600 ml-2'
            )}
          />
        )}
        {message}
      </p>
      {content}
    </>,
    {
      title: heading,
      data: { position: 'bottom-center', ...data },
    }
  );

interface ReturnValue {
  showAlert: typeof showAlert;
  showGeneralErrorMessage: () => void;
  showSuccessMessage: StatusMessage;
  showErrorMessage: StatusMessage;
}

const useShowAlert = (): ReturnValue => {
  const { t } = useTranslation();

  const showSuccessMessage = React.useCallback(
    ({ heading, message, data }) =>
      showAlert({
        type: 'success',
        heading: heading || t('General.SuccessMessageHeading'),
        message,
        data,
      }),
    [t]
  );

  const showErrorMessage = React.useCallback(
    ({ heading, message, data }) =>
      showAlert({
        type: 'error',
        heading: heading || t('General.ErrorMessageHeading'),
        message,
        data: data
          ? {
              duration: 3000,
              ...data,
            }
          : {
              duration: 3000,
            },
      }),
    [t]
  );

  const showGeneralErrorMessage = React.useCallback(
    () =>
      showAlert({
        type: 'error',
        heading: t('General.ErrorMessageHeading'),
        message: t('General.ErrorDescription'),
      }),
    [t]
  );

  return {
    showAlert,
    showSuccessMessage,
    showErrorMessage,
    showGeneralErrorMessage,
  };
};

export default useShowAlert;
